import {useState, memo, ChangeEvent} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {flipOnLTR, media} from '~/shared/theme/utils';
import {attachModal} from '~/shared/components/ReduxModal';
import {PopupModalBase} from '~/shared/components/Modals';
import {createLogger} from '~/shared/logging';
import {headingXS, smallRegular, subheadingSBold} from '~/shared/theme/typography';
import PieButton from '~/shared/theme/pie/PieButton';
import FormPieInput from '~/shared/components/FormField/FormInput/FormPieInput';
import {PieCloseModalButton} from '~/shared/components/Modals/styled';
import ManagerProvider from '~/shared/managers/ManagerProvider';
import {
} from '~/shared/store/selectors';
import store from '~/shared/store';
import {
  setCurrentModal as setCurrentModalAction,
  setDirtyShoppingCart,
  setOrderPermit,
} from '~/shared/store/actions';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import OrderData from '~/shared/store/models/Order/OrderData';

const logger = createLogger('ServiceCodeInputModal');

const Root = styled(PopupModalBase)`
  background-color: ${({theme}) => theme.colors.surface};

  &&& {
    background-color: ${({theme}) => theme.colors.surface};
    width: 100%;
    height: auto;
  }

  ${media.minLargeMobile`
    &&& {
      width: 500px;
    }
  `}
`;

const StyledPieCloseModalButton = styled(PieCloseModalButton)`
  top: 16px;
  ${flipOnLTR`
    left: 16px;
  `}
`;

const ServiceCodeViewHeader = styled.h3`
  ${subheadingSBold}
  padding: 8px 16px 16px;
  color: ${({theme}) => theme.colors.contentDefault};
  width: 100%;
  text-align: start;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  ${media.minLargeMobile`
    ${headingXS}
    padding 16px;
    text-align: center;
  `}
`;

const ServiceCodeViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 24px 16px 40px;
  ${media.minLargeMobile`
    padding: 32px 32px 24px;
  `}
`;

const ActionsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
  ${media.minLargeMobile`
    flex-direction: row;
  `}
`;

const SubmitCodeActionContainer = styled(ActionsContainer)`
  margin-top: 40px;
  ${media.minLargeMobile`
    margin-top: 32px;
  `}
`;

const ActionButton = styled(PieButton)`
  padding: 10px 16px;
  flex: 1;
  ${media.minLargeMobile`
    padding: 12px 16px;
  `}
`;

const ErrorMessage = styled.span`
  ${smallRegular}
  display: block;
  width: 100%;
  color: ${({theme}) => theme.colors.contentError};
  height: 20px;
  margin-top: 4px;
`;

type ModalArgs = {
  onSuccess: (permits: OrderData['permits']) => void;
  modalOptions?: Record<string, unknown>;
};

type ServiceCodeInputModalProps = {
  args: ModalArgs;
  closeModal: () => void;
};

const ServiceCodeInputModal = ({args, closeModal}: ServiceCodeInputModalProps) => {
  const [code, setCode] = useState('');
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {t} = getLocalizationService();

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
  };

  const onSubmit = async (submittedCode: string, onError?: () => void) => {
    try {
      const permitRes = await ManagerProvider.setPermitCodeInOrder(submittedCode);

      if (!permitRes) {
        throw new Error('No response from server');
      }

      store.dispatch(setOrderPermit(permitRes.data.permits));

      store.dispatch(
        setCurrentModalAction('successModal', {
          title: 'the_code_was_submitted_successfully',
          text: 'now_you_can_continue_to_checkout',
          btnText: 'continue',
          disableCloseModal: true,
          onSuccess: () => args?.onSuccess(permitRes.data.permits),
          ...(args?.modalOptions || {}),
        }),
      );
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, onSubmit);
        return;
      }

      if (onError) {
        onError();
      }
      store.dispatch(setDirtyShoppingCart(false));
    }
  };

  const onSubmitHandler = async () => {
    if (!code) {
      logger.warn('the code is empty or the submit handler wasnt passed');
      return;
    }

    setShowError(false);
    try {
      setIsLoading(true);
      await onSubmit(code, () => setShowError(true));
    } catch (error) {
      logger.error('error while submitting the code', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Root>
      <StyledPieCloseModalButton onClick={closeModal} hideOnMobile />
      <ServiceCodeViewHeader id="modal-title">{t('i_have_a_service_code')}</ServiceCodeViewHeader>
      <ServiceCodeViewContainer id="modal-content">
        <FormPieInput
          id="service-code-input"
          placeholder={t('enter_your_code_here')}
          hasError={showError}
          value={code}
          onChange={onChangeHandler}
          aria-autocomplete="none"
          aria-label="service code input"
          data-test-id="service-code-input"
        />
        {showError && <ErrorMessage>{t('this_code_didnt_work_please_double_check')}</ErrorMessage>}
        <SubmitCodeActionContainer>
          <ActionButton
            isLoading={isLoading}
            variant="primaryXL"
            onClick={onSubmitHandler}
            disabled={isLoading || !code?.length}
          >
            {t('accept')}
          </ActionButton>
        </SubmitCodeActionContainer>
      </ServiceCodeViewContainer>
    </Root>
  );
};

ServiceCodeInputModal.mobileBottomSheet = true;
ServiceCodeInputModal.hideCloseButton = true;
attachModal('service_code_input_modal', ServiceCodeInputModal);

export default memo(ServiceCodeInputModal);
